import React, { useEffect, useState } from "react";
import { FormGroup, InputGroup, Page, required, SubmitButton, } from "components/common";
import Select from "react-select";
import { actions, Control, Errors } from "react-redux-form";
import { NotificationManager } from "react-notifications";
import { useForm } from "hooks";
import Dropzone from "react-dropzone";
import { Trans } from "react-i18next";
import { isDecimal, isEmail } from "validator";
import { Alert, Button, Col, Input, Row } from "reactstrap";
import { humanError } from "services/util";
import { validationMessage } from "constants/validationMsg";
import BackButton from '../PayrollHrNew/BackButton'
import { useXeroClient } from "../../hooks";
import { createPortal } from 'react-dom';
import { useBaseModal } from "services";

const isPercent = (v) => {
  if (!isDecimal(v, { decimal_places: "0,2" })) return false;
  return v >= 0 && v <= 100;
};

export const Settings = ({ payroll, t, url = "settings", xero }) => {
  const { dispatch, model, setValue, value, Form, save, loaded, load } =
    useForm();
  const id = 1;
  const client = useXeroClient();

  const [accounts, setAccounts] = useState([]);
  const [bankAccounts, setbankAccount] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [allowanceRules, setAllowanceRules] = useState([]);
  const [allprovisions, setAllProvision] = useState([]);
  const [allDeductions, setDeductions] = useState([]);
  const [isLoading, setIsLoading] = useState(false)

  const [setConfirmOpen, ConfirmToggle, ConfirmModal] = useBaseModal();
  const handleChange = (e) => {
    const newValue = e.target.value;
    if (/^\d*$/.test(newValue)) {
      setValue({ ...value, next_employee_number: newValue })
    } else {
      setValue({ ...value, next_employee_number: value.next_employee_number })
    }
  };

  useEffect(() => {
    async function fetchData() {
      const item = await load(payroll, url, id, {
        logo: null,
        payslip_text: "",
        organisation_admin_email: "",
        local_tax_rate: "0.00",
        foreigner_tax_rate: "0.00",
        allow_half_day_holidays: true,
      });

      const response_accounts = await client.get('accounts', {
        params: {
          raw: `Status="ACTIVE"`,
        },
      },).get("data").filter(item => !item.SystemAccount);

      let bankAccounts = []
      let accounts = []

      for (var i = 0; i < response_accounts.length; i++) {
        // Iterate over numeric indexes from 0 to 5, as everyone expects.
        const el = response_accounts[i]
        if (el.Type === "BANK") {
          bankAccounts.push({
            label: `${el.Name}`,
            value: el.AccountID,
          })
        }
        else {
          accounts.push({
            label: `${el.Name} (${el.Code})`,
            value: el.Code,
          })
        }
      }

      const contacts = await xero
        .get("contacts")
        .get("data")
        .map((x) => ({
          label: x.Name,
          value: x.ContactID,
        }));
      const allowances = await payroll
        .get("allowance_rules")
        .get("data")
        .map((x) => ({
          label: x.description,
          id: x.id,
          allowance_debit_account: x.allowance_debit_account,
        })).filter(item => item.label !== "Other Allowances");

      const provisions = await payroll
        .get("provisions")
        .get("data")
        .map((x) => ({
          label: x.description,
          id: x.id,
          provision_credit_account: x.provision_credit_account,
        }));

      const deductions = await payroll
        .get("deductions")
        .get("data")
        .map((x) => ({
          label: x.description,
          id: x.id,
          deduction_debit_account: x.deduction_debit_account,
        }));
      setAccounts(accounts);

      setbankAccount(bankAccounts);
      setContacts(contacts);
      setAllowanceRules(allowances);
      setDeductions(deductions);
      setAllProvision(provisions)
      const allowancesList = allowances.reduce((allowance, item) => ({
        ...allowance, [item.id]:
          { allowance_debit_account: item.allowance_debit_account }
      }), {});
      const deductionList = deductions.reduce((deduction, item) => ({
        ...deduction, [item.id]:
          { deduction_debit_account: item.deduction_debit_account }
      }), {})
      const provisionList = provisions.reduce((provision, item) => ({
        ...provision, [item.id]:
          { provision_credit_account: item.provision_credit_account }
      }), {})
      setValue({ ...item, allowances: allowancesList, deductions: deductionList, provisions: provisionList });
    };
    fetchData();
  }, [id]);

  function onDropAccepted([file]) {
    const reader = new FileReader();
    reader.onload = (e) => {
      dispatch(actions.change("local.logo", e.target.result));
    };
    reader.readAsDataURL(file);
  }

  function onLogoError() {
    dispatch(actions.setErrors("local.logo", { invalid: true }));
  }

  function onLogoLoad() {
    dispatch(actions.setErrors("local.logo", { invalid: false }));
  }

  function settings_setup() {
    setIsLoading(true)
    payroll.post('settings/populate').then((resp) => {
      NotificationManager.success(t('Setup Completed'))
      setIsLoading(false)
      window.location.reload();
    }).catch((err) => {
      NotificationManager.error(t('Setup Failed') + " " + err.message)
      setIsLoading(false)
    }
    )
  }

  async function onSubmit(item) {
    try {
      if (!item.contact || item.contact === null) {
        NotificationManager.error(t("Please Enter a Contact For Transfer."));
        return;
      }
      await save(payroll, url, item);
      NotificationManager.success(t("Saved successfully"));
    } catch (error) {
      const error_message = humanError(error, "Error happened while saving", t)
      NotificationManager.error(error_message);
    } finally {
      dispatch(actions.setPending(model, false));
    }
  }


  const AccountSelect = ({ label, model, required = true }) => (
    <FormGroup
      label={required ? `${label} *` : label}
      model={model}
      addControlClass={false}
    >
      <Control.text
        model={model}
        component={Select}
        options={accounts}
        simpleValue
        clearable={false}
      />
      <Errors
        className="form-text small text-danger"
        show="touched"
        model={model}
        messages={{ required: t(validationMessage.REQUIRED) }}
      />
    </FormGroup>
  );

  const BankAccountSelect = ({ label, model, required = true }) => (
    <FormGroup
      label={required ? `${label} *` : label}
      model={model}
      addControlClass={false}
    >
      <Control.text
        model={model}
        component={Select}
        options={bankAccounts}
        simpleValue
        clearable={false}
      />
    </FormGroup>
  );
  return (
    <>
      {loaded && isLoading &&
        createPortal(
          <div className="content">
            <div className="loader"></div>
          </div>,
          document.querySelector('#loader-holder')
        )
      }
      {loaded && <Page title="">
        <BackButton />
        <h4 style={{ marginTop: "15px", marginBottom: "20px" }}><Trans>Settings</Trans></h4>
        <Form
          onSubmit={onSubmit}
          validators={{
            organisation_admin_email: (v) => (v ? isEmail(v) : true),
            credit_account: { required },
            debit_account: { required },
            basic_salary_debit_account: { required },
            overtime_debit_account: { required },
            allowances_debit_account: { required },
            benefit_claim_debit_account: { required },
            salary_loan_debit_account: { required },
            reimbursement_debit_account: { required },
            withholding_tax_credit_account: { required },
            insurance_shares_credit_account: { required },
            salary_loan_credit_account: { required },
            other_deductible_credit_account: { required },
            salaries_payable_credit_account: { required },
            annual_leave_expenses_debit_account: { required },
            bad_debts_provision_debit_account: { required },
            annual_leave_provision_credit_account: { required },
            employee_bill_account: { required },
            local_tax_rate: isPercent,
            foreigner_tax_rate: isPercent,
          }}
        >
          <FormGroup
            label="Allow Half Day Holidays"
            model=".allow_half_day_holidays"
            addControlClass={false} className="pt-2 pb-2"
          >
            <Control.text style={{ backgroundColor: '#F4F7FE' }}
              model=".allow_half_day_holidays"
              component={Select}
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
              simpleValue
              clearable={false}
            />
          </FormGroup>
          <FormGroup
            label="Weekends as leaves"
            model=".weekend_as_leave"
            addControlClass={false} className="pt-2 pb-2"
          >
            <Control.text style={{ backgroundColor: '#F4F7FE' }}
              model=".weekend_as_leave"
              component={Select}
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
              simpleValue
              clearable={false}
            />
          </FormGroup>
          <FormGroup label="Logo" model=".logo" className="pt-2 pb-2">
            <Control.custom
              model=".logo"
              render={() => (
                <div>
                  <Dropzone style={{ backgroundColor: '#F4F7FE', borderRadius: '4px' }}
                    multiple={false}
                    className="border border-primary rounded"
                    onDropAccepted={onDropAccepted}
                  >
                    <div className="m-2 text-center">
                      {value.logo ? (
                        <div>
                          <img
                            alt="logo"
                            onLoad={onLogoLoad}
                            onError={onLogoError}
                            className="img-thumbnail mb-3"
                            src={value.logo}
                          />
                        </div>
                      ) : null}
                      <span>
                        <Trans>
                          Drop an image file here, or click to select a file to
                          upload.
                        </Trans>
                      </span>
                    </div>
                  </Dropzone>
                  <Errors
                    model=".logo"
                    component={(props) => (
                      <p className="form-text small text-danger" {...props} />
                    )}
                    messages={{
                      invalid: t(validationMessage.INVALID_IMAGE_FILE),
                    }}
                  />
                </div>
              )}
            />
          </FormGroup>
          <FormGroup label="Payslip Text" model=".payslip_text" className="pt-2 pb-2">
            <Control.textarea rows="4" model=".payslip_text" updateOn="blur" style={{ backgroundColor: '#F4F7FE' }} />
          </FormGroup>
          <FormGroup label="Next Employee Serial Number" model=".next_employee_number" className="pt-2 pb-2">
            <Input model=".next_employee_number" updateOn="blur" inputMode="numeric"
              value={value.next_employee_number}
              onChange={(e) => handleChange(e)} defaultValue={value.next_employee_number} />
          </FormGroup>
          {/* <FormGroup label="Local Tax Rate *" model=".local_tax_rate" className="pt-2 pb-2">
            <InputGroup suffixIcon="fa-percent">
              <Control.text model=".local_tax_rate" updateOn="blur" />
            </InputGroup>
          </FormGroup>
          <FormGroup label="Foreigner Tax Rate *" model=".foreigner_tax_rate" className="pt-2 pb-2">
            <InputGroup suffixIcon="fa-percent">
              <Control.text model=".foreigner_tax_rate" updateOn="blur" />
            </InputGroup>
          </FormGroup> */}
          <FormGroup
            label="Organisation Admin Email"
            model=".organisation_admin_email" className="pt-2 pb-2"
          >
            <Control.text model=".organisation_admin_email" updateOn="blur" />
          </FormGroup>

          <FormGroup
            label="Contact to transfer pay run money *"
            model=".contact"
            addControlClass={false}
          >
            <Control.text style={{ backgroundColor: '#F4F7FE' }}
              model=".contact"
              component={Select}
              options={contacts}
              simpleValue
              clearable={false}
            />
          </FormGroup>

          {/*{allowanceRules.length > 0 ? (*/}
          <>
            <div className="row justify-content-between align-items-center">
              <div className="col-6"><h4 className="mt-4 mb-3"><Trans>Accounts</Trans></h4></div>
            </div>
            <Row>
              <Col className="col-border-right">
                <b><Trans>Debit Accounts</Trans></b>
              </Col>
              <Col>
                <b><Trans>Credit Accounts</Trans></b>
              </Col>
            </Row>
            <Row>
              <Col className="col-border-right">
                <i className="text-primary"><Trans>Used on Payrun Approval</Trans></i>
              </Col>
              <Col></Col>
            </Row>
            <Row>
              <Col className="col-border-right pt-2 pb-2" >
                <AccountSelect
                  label="Basic Salary"
                  model=".basic_salary_debit_account"
                />
              </Col>
              <Col className="pt-2 pb-2">
                <AccountSelect
                  label="Withholding Tax"
                  model=".withholding_tax_credit_account"
                />
              </Col>
            </Row>
            <Row>
              <Col className="col-border-right pt-2 pb-2">
                <AccountSelect label="Overtime" model=".overtime_debit_account" />
              </Col>
              <Col className="pt-2 pb-2">
                <AccountSelect
                  label="Insurance Shares"
                  model=".insurance_shares_credit_account"
                />
              </Col>
            </Row>
            <Row>
              <Col className="col-border-right pt-2 pb-2 col-6">
                <AccountSelect label="Late time" model=".latetime_credit_account" />
              </Col>
            </Row>
            <Row>
              <Col className="col-border-right pt-2 pb-2">
                <fieldset className="border p-2">
                  {allowanceRules.length > 0 && (
                    <div>
                      <legend className="w-auto"><Trans>Allowances</Trans></legend>
                      {allowanceRules.map((i) => (
                        <FormGroup key={i.id}
                          label={`${i.label} *`}
                          model={`.allowances[${i.id}].allowance_debit_account`}
                          addControlClass={false}
                        >
                          <Control.text
                            model={`.allowances[${i.id}].allowance_debit_account`}
                            component={Select}
                            options={accounts}
                            simpleValue
                            clearable={false}
                            required={true}
                            validators={{ required }}
                          />
                          <Errors
                            className="form-text small text-danger"
                            show="touched"
                            model={`.allowances[${i.id}].allowance_debit_account`}
                            messages={{ required: t(validationMessage.REQUIRED) }}
                          />
                        </FormGroup>
                      ))}
                    </div>
                  )}

                  <AccountSelect
                    label="Other Allowances"
                    model=".allowances_debit_account"
                  />
                </fieldset>
              </Col>

              <Col className="pt-2 pb-2">
                <AccountSelect
                  label="Salary Loan"
                  model=".salary_loan_credit_account"
                />
                <AccountSelect
                  label="Bad Debts Provision"
                  model=".bad_debts_provision_debit_account"
                />
              </Col>
            </Row>
            <Row className="mt-3 mb-2">
              {allDeductions.length > 0 && (
                <Col className="col-border-right">
                  <fieldset className="border p-2">
                    <legend className="w-auto"><Trans>Deductions</Trans></legend>
                    {allDeductions.map((i) => (
                      <FormGroup key={i.id}
                        label={`${i.label} *`}
                        model={`.deductions[${i.id}].deduction_debit_account`}
                        addControlClass={false}
                      >
                        <Control.text
                          model={`.deductions[${i.id}].deduction_debit_account`}
                          component={Select}
                          options={accounts}
                          simpleValue
                          clearable={false}
                          required={true}
                          validators={{ required }}
                        />
                      </FormGroup>
                    ))}
                  </fieldset>
                </Col>)}
              {allprovisions.length > 0 && (
                <Col>
                  <fieldset className="border p-2">
                    <legend className="w-auto"><Trans>Provisions</Trans></legend>
                    {allprovisions.map((i) => (
                      <FormGroup key={i.id}
                        label={`${i.label} *`}
                        model={`.provisions[${i.id}].provision_credit_account`}
                        addControlClass={false}
                      >
                        <Control.text
                          model={`.provisions[${i.id}].provision_credit_account`}
                          component={Select}
                          options={accounts}
                          simpleValue
                          clearable={false}
                          required={true}
                          validators={{ required }}
                        />
                      </FormGroup>
                    ))}
                  </fieldset>
                </Col>)}
            </Row>
            <Row className="mt-3 mb-2">
              <Col className="col-border-right">
                <AccountSelect
                  label="Benefit Claim"
                  model=".benefit_claim_debit_account"
                />
              </Col>
              <Col>
                <AccountSelect
                  label="Other Deductible"
                  model=".other_deductible_credit_account"
                />
              </Col>
            </Row>
            <Row className="mt-3 mb-2">
              <Col className="col-border-right">
                <AccountSelect
                  label="End Of Service Expenses"
                  model=".eos_expense_account"
                />
              </Col>
              <Col>
                <AccountSelect
                  label="End Of Service Provision"
                  model=".eos_provision_account"
                />
              </Col>
            </Row>
            <Row className="mt-3 mb-2">
              <Col className="col-border-right">
                <AccountSelect
                  label="Salary Loan"
                  model=".salary_loan_debit_account"
                />
              </Col>
              <Col>
                <AccountSelect
                  label="Salaries Payable"
                  model=".salaries_payable_credit_account"
                />
              </Col>
            </Row>
            <Row className="mt-3 mb-2">
              <Col className="col-border-right">
                <AccountSelect
                  label="Annual Leave Expenses"
                  model=".annual_leave_expenses_debit_account"
                />
              </Col>
              <Col>
                <AccountSelect
                  label="Annual Leave Provision"
                  model=".annual_leave_provision_credit_account"
                />
              </Col>
            </Row>
            <Row className="mt-3 mb-2">
              <Col className="col-border-right">
                <AccountSelect
                  label="Reimbursement"
                  model=".reimbursement_debit_account"
                />
              </Col>
              <Col></Col>
            </Row>
            <Row className="mt-3 ">
              <Col className="col-border-right">
                <i className="text-primary"><Trans>Used on payroll payment</Trans></i>
              </Col>
              <Col></Col>
            </Row>
            <Row className="mt-1 mb-2">
              <Col className="col-border-right">
                <AccountSelect label="Salaries Payable" model=".debit_account" />
              </Col>
              <Col>
                <BankAccountSelect label="Bank Account" model=".credit_account" />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <i className="text-primary">
                  <Trans>Used for employee net payment bills</Trans>
                </i>
              </Col>
              <Col></Col>
            </Row>
            <Row className="mb-2">
              <Col className=" pb-2 pt-2">
                <AccountSelect
                  label="Employee Bills Account"
                  model=".employee_bill_account"
                />
              </Col>
              <Col></Col>
            </Row>
          </>
          {/*) : (*/}
          {/* <h4 className="mb-3 mt-3"><Trans>End of Service Rules</Trans></h4>
          <Table className="table no-wrap v-middle text-center" responsive>
            <thead>
              <tr>
                <th width="20%" />
                <th className="text-nowrap"><Trans>Years of Service</Trans></th>
                <th className="text-nowrap"><Trans>Days per Year</Trans></th>
                <th className="text-nowrap"><Trans>Local</Trans>/<Trans>Foreigner</Trans></th>
                <th />
              </tr>
            </thead>
            <tbody>
              {value.end_of_service_rules.map((x, i) => {
                const filteredNums = (value) => value && /\D/.test(value);
                const daysPerYear = value.end_of_service_rules[i].days_per_year;
                const yearsOfService = value.end_of_service_rules[i].years_of_service;

                return (
                  <>
                    <tr key={i}>
                      <td>
                        <Control.text
                          model={`.end_of_service_rules[${i}].is_first_x_years`}
                          component={Select}
                          options={[
                            { value: true, label: "Up To" },
                            { value: false, label: "More Than" },
                          ]}
                          simpleValue
                          clearable={false}
                        />
                      </td>
                      <td>
                        <FormGroup
                          model={`.end_of_service_rules[${i}].years_of_service`}
                        >
                          <Control.text
                            model={`.end_of_service_rules[${i}].years_of_service`}
                          />
                          {filteredNums(yearsOfService) && <span style={{ color: 'red' }}>Please enter a whole number. (Upto/More Than)</span>}
                        </FormGroup>
                      </td>
                      <td>
                        <FormGroup
                          model={`.end_of_service_rules[${i}].days_per_year`}
                        >
                          <Control.text
                            model={`.end_of_service_rules[${i}].days_per_year`}
                            className="form-control"
                          />
                          {filteredNums(daysPerYear) && <span style={{ color: 'red' }}>Please enter a whole number.</span>}
                        </FormGroup>
                      </td>
                      <td>
                        <Control.text
                          model={`.end_of_service_rules[${i}].is_local`}
                          component={Select}
                          options={[
                            { value: true, label: "Local" },
                            { value: false, label: "Foreigner" },
                          ]}
                          simpleValue
                          clearable={false}
                        />
                      </td>
                      <td className="text-right">
                        <Button
                          color="link"
                          className="text-danger"
                          onClick={() => {
                            dispatch(actions.remove("local.end_of_service_rules", i));
                          }}
                        >
                          <span className="fa fa-trash" />
                        </Button>
                      </td>
                    </tr>
                  </>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5">
                  <button
                    type="button"
                    onClick={onAddRule}
                    className="btn btn-primary"
                  >
                    + <Trans>Add Rule</Trans>
                  </button>
                </td>
              </tr>
            </tfoot>
          </Table> */}
          <div className="row">
            <div className="col-6">
              <Button className="btn btn-primary ml-0" onClick={ConfirmToggle}><Trans>Setup</Trans></Button>
            </div>
            <div className="col-6 text-right">
              <SubmitButton>Save</SubmitButton>
            </div>
          </div>
        </Form>
        <ConfirmModal title={t("Confirm Setup")} HandleSubmit={() => { settings_setup(); setConfirmOpen(false) }} action="Confirm">
          <div className="d-block">
            <Alert color="warning">
              <Trans>
                Current selected accounts will be reset, are you sure you want to continue?
              </Trans>
            </Alert>
          </div>
        </ConfirmModal>
      </Page>
      }
    </>

  );
};

export default Settings;
